import {getCookieValueIfConsent} from '@/cookieshandler';
import {validateEmailOnChange} from '@/formshandler/custom-email-validation';
import {sendDataLayerEvent} from '../../utils/analytics';

/**
 * @param {function} condition
 * @param {function} callback
 * @param {int} interval
 * @param {int} tryCount
 */
const waitUntil = (condition, callback, interval, tryCount) => {
  let waitCount = 0;
  const waitUntilTimer = setInterval(() => {
    waitCount += 1;

    if (condition()) {
      callback();
      clearInterval(waitUntilTimer);
    }

    if (waitCount >= tryCount) {
      clearInterval(waitUntilTimer);
    }
  }, interval);
};

export default class FormsHandler {
  /**
   * This method handles form submit to send errors in heap as a custom event.
   *
   * @param {string} formId
   */
  static handleFormSubmit(formId) {
    const form = document.querySelector(`[data-form-id='${formId}']`);
    const waitInterval = 150;
    const waitTryCount = 15;

    if (form) {
      form.addEventListener('submit', () => {
        waitUntil(
          () => form.querySelector('.hs-form-field .hs-error-msg') !== null,
          () => {
            const fields = form.querySelectorAll('.hs-form-field');
            const errors = [];

            [].forEach.call(fields, field => {
              const error = field.querySelector('.hs-error-msg');
              const input = field.querySelector('input, select');

              if (input && error) {
                const errorMsg = `${input.getAttribute('name') ? input.getAttribute('name') : input.getAttribute('id')} : ${error.innerText}`;
                errors.push(errorMsg);
              }
            });

            if (errors.length > 0) {
              sendDataLayerEvent('demoSubmitError', {
                demoErrorName: errors.join(' , ')
              });
            }
          },
          waitInterval,
          waitTryCount
        );
      });
    }
  }

  static handleInputFocus(formID) {
    const hsFormFields = document.querySelectorAll(`[data-form-id="${formID}"] .hs-form-field`);

    // NodeList.forEach not supported by IE10+
    Array.from(hsFormFields).forEach(element => {
      const inputElements = element.querySelectorAll('.hs-input');
      const inputLabel = element.querySelector('label');

      const checkExpanded = () => {
        // detect double line label text
        if (inputLabel && inputLabel.offsetHeight >= 36) {
          element.classList.add('hs-field-expanded');
        }
      };

      Array.from(inputElements).forEach(inputElement => {
        if (inputElement.nodeName === 'SELECT') {
          inputElement.querySelector('option:first-child').innerHTML = '';
        }

        // Consider browser autocomplete and add focus.
        if (
          (inputElement.getAttribute('type') === 'checkbox' && inputElement.checked) ||
          (inputElement.getAttribute('type') !== 'checkbox' && inputElement.value !== '')
        ) {
          element.classList.add('focused');
        }

        checkExpanded();

        inputElement.onfocus = () => {
          element.classList.add('focused');
          checkExpanded();
        };

        inputElement.onblur = () => {
          if (inputElement.value === '') {
            element.classList.remove('focused');
          }
        };

        inputElement.onchange = checkExpanded;
      });
    });
  }

  static populateGCLIDValue() {
    // Add gclid value.
    const gclidInputs = document.querySelectorAll('input[name="gclid"]');
    if (gclidInputs) {
      const gclidCookie = getCookieValueIfConsent('_gcl_aw');
      if (gclidCookie) {
        const gclidSplit = gclidCookie.split('.');
        const gclidValue = gclidSplit[gclidSplit.length - 1];

        gclidInputs.forEach(element => {
          element.setAttribute('data-gclid', '');
          element.setAttribute('value', gclidValue);
        });
      }
    }
  }

  static populateMetroValue() {
    // Add metro value.
    const metroInputs = document.querySelectorAll('input[name="metro_visitor_id__c"]');
    if (metroInputs) {
      const wmcCookie = getCookieValueIfConsent('wmc');
      if (wmcCookie) {
        const {cookie_id = ''} = JSON.parse(wmcCookie);
        metroInputs.forEach(element => {
          element.setAttribute('value', cookie_id);
        });
      }
    }
  }

  static populateCompanyFromEmailInBForm(formId) {
    const form = document.querySelector(`[data-form-id='${formId}']`);
    const emailInput = form.querySelector('input[name="email"]');
    const companyInput = form.querySelector('input[name="company"][type="hidden"]');

    if (emailInput && companyInput) {
      emailInput.addEventListener('change', () => {
        const emailArray = emailInput.value.split('@');

        if (emailArray.length > 1) companyInput.value = `${emailArray[1]}[VariantB]`;
      });
    }
  }

  static handleFormError() {
    const mobileWidth = 992;
    const scrollOffset = 200;
    const waitInterval = 50;

    setTimeout(() => {
      const errorElements = document.querySelectorAll('.hs-error-msgs li');

      // Scroll to the first error message if the screen is mobile and there are errors.
      if (window.innerWidth < mobileWidth && errorElements.length) {
        const errorScrollTop =
          errorElements[0].getBoundingClientRect().top + window.scrollY - scrollOffset;
        window.scrollTo({top: errorScrollTop, behavior: 'smooth'});
      }
    }, waitInterval);
  }

  static handleFormReady() {
    // Remove the custom style class from the form
    const element = document.querySelector('.hs-custom-style');
    if (element) element.classList.remove('hs-custom-style');
  }

  static handleDemoFormReady(formId) {
    FormsHandler.handleInputFocus(formId);

    FormsHandler.handleFormSubmit(formId);

    // This used by a revops as a backup for the company name
    FormsHandler.populateCompanyFromEmailInBForm(formId);

    FormsHandler.populateGCLIDValue();

    FormsHandler.populateMetroValue();

    validateEmailOnChange(formId);
  }
}
